import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { FaSearch, FaFilter, FaSortAmountDown, FaTag } from 'react-icons/fa'

import { useTranslation } from 'react-i18next'

export default function BlogList() {
    const { t, i18n } = useTranslation()
    const [searchParams, setSearchParams] = useSearchParams()
    const [allPosts, setAllPosts] = useState([]) // Stores all published posts
    const [posts, setPosts] = useState([]) // Stores filtered posts for display
    const [allTags, setAllTags] = useState([])
    const [loading, setLoading] = useState(true)

    // Filters
    const [searchQuery, setSearchQuery] = useState('')
    const [timeFilter, setTimeFilter] = useState('all') // all, week, month, year
    const [sortOrder, setSortOrder] = useState('latest') // latest, oldest
    const [selectedTag, setSelectedTag] = useState(searchParams.get('tag') || '')

    // 1. Fetch ALL published posts on mount (once)
    useEffect(() => {
        async function fetchAllPosts() {
            setLoading(true)
            try {
                const { data, error } = await supabase
                    .from('posts')
                    .select('*')
                    .eq('is_published', true)
                    .order('created_at', { ascending: false })

                if (error) throw error

                if (data) {
                    setAllPosts(data)
                    // Extract unique tags from ALL published posts
                    const tags = [...new Set(data.flatMap(post => post.tags || []))]
                    setAllTags(tags.sort())
                }
            } catch (error) {
                console.error('Error fetching posts:', error.message)
            } finally {
                setLoading(false)
            }
        }
        fetchAllPosts()
    }, [])

    // 2. Filter & Sort functionality (runs on filter change)
    useEffect(() => {
        let result = [...allPosts]

        // Search Filter (Title & Content in both languages + Slug)
        if (searchQuery) {
            const q = searchQuery.toLowerCase()
            result = result.filter(post => {
                const titleEn = (post.title_en || post.title || '').toLowerCase()
                const titleEs = (post.title_es || '').toLowerCase()
                const contentEn = (post.content_en || post.content || '').toLowerCase()
                const contentEs = (post.content_es || '').toLowerCase()
                const slug = (post.slug || '').toLowerCase()

                return titleEn.includes(q) ||
                    titleEs.includes(q) ||
                    contentEn.includes(q) ||
                    contentEs.includes(q) ||
                    slug.includes(q)
            })
        }

        // Time Filter
        if (timeFilter !== 'all') {
            const now = new Date()
            let startDate = new Date()

            if (timeFilter === 'week') startDate.setDate(now.getDate() - 7)
            if (timeFilter === 'month') startDate.setMonth(now.getMonth() - 1)
            if (timeFilter === 'year') startDate.setFullYear(now.getFullYear() - 1)

            result = result.filter(post => new Date(post.created_at) >= startDate)
        }

        // Tag Filter
        if (selectedTag) {
            result = result.filter(post => (post.tags || []).includes(selectedTag))
        }

        // Sort
        result.sort((a, b) => {
            const dateA = new Date(a.created_at)
            const dateB = new Date(b.created_at)
            return sortOrder === 'oldest' ? dateA - dateB : dateB - dateA
        })

        setPosts(result)
    }, [allPosts, searchQuery, timeFilter, selectedTag, sortOrder])

    // Sync URL param for tag
    useEffect(() => {
        if (selectedTag) {
            setSearchParams({ tag: selectedTag }, { replace: true })
        } else {
            setSearchParams({}, { replace: true })
        }
    }, [selectedTag])

    // Removed the fetchAllTags separate effect since effective tags are derived from allPosts


    const clearAllFilters = () => {
        setSearchQuery('')
        setTimeFilter('all')
        setSelectedTag('')
    }

    return (
        <div className="container mx-auto px-4 py-12">
            <h1 className="text-4xl font-bold mb-8 text-center text-primary">{t('blog_page.title')}</h1>

            {/* Search & Filter Bar */}
            <div className="bg-white border border-gray-200/50 rounded-xl p-4 mb-4 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">

                {/* Search */}
                <div className="relative w-full md:w-1/3">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-ash" />
                    <input
                        type="text"
                        placeholder={t('blog_page.search_placeholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-gray-100/50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-base"
                    />
                </div>

                <div className="flex gap-4 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">
                    {/* Time Filter */}
                    <div className="flex items-center gap-2">
                        <FaFilter className="text-ash" />
                        <select
                            value={timeFilter}
                            onChange={(e) => setTimeFilter(e.target.value)}
                            className="bg-gray-100/50 border border-gray-200 rounded-lg px-3 py-2 text-base text-ink focus:outline-none focus:ring-2 focus:ring-primary/50"
                        >
                            <option value="all">{t('blog_page.filter.all')}</option>
                            <option value="week">{t('blog_page.filter.week')}</option>
                            <option value="month">{t('blog_page.filter.month')}</option>
                            <option value="year">{t('blog_page.filter.year')}</option>
                        </select>
                    </div>

                    {/* Sort Order */}
                    <div className="flex items-center gap-2">
                        <FaSortAmountDown className="text-ash" />
                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            className="bg-gray-100/50 border border-gray-200 rounded-lg px-3 py-2 text-base text-ink focus:outline-none focus:ring-2 focus:ring-primary/50"
                        >
                            <option value="latest">{t('blog_page.sort.latest')}</option>
                            <option value="oldest">{t('blog_page.sort.oldest')}</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Tag Filter Pills */}
            {allTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-8 items-center">
                    <FaTag className="text-ash text-sm" />
                    <button
                        onClick={() => setSelectedTag('')}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border ${!selectedTag
                            ? 'bg-primary text-ink border-primary shadow-md'
                            : 'bg-gray-100/50 text-ash border-gray-200 hover:border-primary/50 hover:text-ink'
                            }`}
                    >
                        {t('blog_page.all_tags')}
                    </button>
                    {allTags.map((tag) => (
                        <button
                            key={tag}
                            onClick={() => setSelectedTag(tag === selectedTag ? '' : tag)}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border ${selectedTag === tag
                                ? 'bg-primary text-ink border-primary shadow-md'
                                : 'bg-gray-100/50 text-ash border-gray-200 hover:border-primary/50 hover:text-ink'
                                }`}
                        >
                            {tag}
                        </button>
                    ))}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                </div>
            ) : posts.length === 0 ? (
                <div className="text-center text-ash py-20 bg-gray-100/20 rounded-xl border border-dashed border-gray-200">
                    <p className="text-xl font-medium">{t('blog_page.no_posts_found')}</p>
                    <button
                        onClick={clearAllFilters}
                        className="mt-4 text-primary hover:underline"
                    >
                        {t('blog_page.clear_filters')}
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {posts.map((post) => {
                        const isEs = i18n.language === 'es'

                        // Strict logic: Show translation if exists, otherwise show ANY available title (Original fallback)
                        // If we are in ES: Try ES title -> Try EN title -> Try legacy Title
                        // If we are in EN: Try EN title -> Try ES title -> Try legacy Title
                        // This ensures "Not appearing" never happens as long as ONE title exists.
                        const title = isEs
                            ? (post.title_es || post.title_en || post.title || 'Untitled')
                            : (post.title_en || post.title || post.title_es || 'Untitled')

                        const excerpt = isEs
                            ? (post.excerpt_es || post.excerpt_en || post.excerpt)
                            : (post.excerpt_en || post.excerpt || post.excerpt_es)

                        return (
                            <Link key={post.id} to={`/blog/${post.slug}`} className="group">
                                <div className="bg-white rounded-xl overflow-hidden shadow-lg transition-transform duration-300 group-hover:-translate-y-2 border border-gray-200/50 h-full flex flex-col">
                                    {post.image_url && (
                                        <div className="h-48 overflow-hidden relative">
                                            <img
                                                src={post.image_url}
                                                alt={title}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                            />
                                            <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded">
                                                {new Date(post.created_at).toLocaleDateString(i18n.language)}
                                            </div>
                                        </div>
                                    )}
                                    {!post.image_url && (
                                        <div className="h-48 bg-gray-100 flex items-center justify-center text-ash">
                                            {t('blog_page.no_image')}
                                        </div>
                                    )}
                                    <div className="p-6 flex-1 flex flex-col">
                                        <h2 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors line-clamp-2">
                                            {title}
                                        </h2>
                                        <p className="text-ash line-clamp-3 mb-4 flex-1">
                                            {excerpt}
                                        </p>
                                        {/* Tags */}
                                        {post.tags && post.tags.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 mb-4">
                                                {post.tags.map((tag) => (
                                                    <span
                                                        key={tag}
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium border border-primary/20"
                                                    >
                                                        <FaTag className="text-[8px]" />
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <div className="mt-auto pt-4 border-t border-gray-200/50 flex justify-between items-center text-sm">
                                            <span className="text-primary font-medium group-hover:underline">{t('blog_page.read_more')} &rarr;</span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
